<template>
  <div class="d-block d-lg-flex">
    <div
      class="wishlists-icon-container"
      :class="{ 'collection-icon': forCollection }"
      @click="onClick"
    >
      <font-awesome-icon
        :icon="wishlist ? ['fas', 'heart'] : ['far', 'heart']"
        :class="['icon-heart', inWishlist && 'icon-heart-red', canChange && 'cursor-pointer']"
        size="2x"
      />
    </div>
    <span
      v-if="!forCollection && dealsCountFormatted"
      class="favorite-count"
    >
      {{ dealsCountFormatted }}
    </span>

    <WishlistsListingModal :show-modal.sync="showWishlistModal" :deal-id="dealId" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import WishlistsListingModal from './ListingModal.vue';

export default {
  components: {
    FontAwesomeIcon,
    WishlistsListingModal,
  },

  props: {
    dealsCount: {
      type: Number,
      required: false,
      default: 0,
    },

    dealId: {
      type: Number,
      required: true,
    },

    forCollection: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showWishlistModal: false,
    };
  },

  computed: {
    requestErrorMessage() {
      return this.$store.getters['wishlists/errorMessage'];
    },

    wishlist() {
      return this.$store.getters['wishlists/wishlist'];
    },

    wishlists() {
      return this.$store.getters['wishlists/wishlists'];
    },

    canChange() {
      // only for collections, do we care if user is owner
      if (this.forCollection) {
        return this.wishlist?.is_owner;
      }
      return true;
    },

    wishlistDeal() {
      return this.wishlist && this.wishlist.deals.find(
        (deal) => deal.id === this.dealId,
      );
    },

    dealsCountFormatted() {
      // Format number to compact notation, example: 16500 to 16.5K
      const formatter = Intl.NumberFormat('en', {
        notation: 'compact',
        minimumIntegerDigits: 1,
        maximumFractionDigits: 2,
      });

      return this.dealsCount && formatter.format(this.dealsCount);
    },

    inWishlist() {
      if (this.forCollection) {
        // special logic for when we have multiple deals in a collection page
        return this.wishlist && !!this.wishlist.deals.find(
          (deal) => deal.id === this.dealId,
        );
      }
      // the way saveList works, is it doesn't fetch the updated list
      // so we just need to check if the wishlist is in the store
      // which indicates we have selected and added to a list
      return this.wishlist;
    },
  },

  async mounted() {
    // if we're on a deal page, update to list where deal exists
    if (!this.forCollection) {
      // Search deal in wishlists
      const wishlist = this.wishlists.find(
        (x) => x?.deals.find((y) => y.id === this.dealId),
      );

      await this.$store.dispatch('wishlists/setWishlist', wishlist);
    }
  },

  methods: {
    async onClick(event) {
      if (!this.canChange) {
        // do nothing if we can't edit
        return;
      }

      this.$emit('click', event);

      if (!this.forCollection) {
        this.showWishlistModal = true;
      } else if (this.inWishlist) {
        await this.removeFromList();
      } else {
        await this.saveList();
      }
    },

    async removeFromList() {
      if (this.isProcessing) {
        return;
      }

      await this.$store.dispatch('wishlists/removeFromList', { wishlistDealId: this.wishlistDeal.wishlist_deal_id });

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      this.$toastr.s(`Removed from <strong>${this.wishlist.name}</strong> wishlist!`);

      this.$store.dispatch('wishlists/getWishlistsHeaderData', this.wishlist.slug);
    },

    async saveList() {
      if (this.isProcessing) {
        return;
      }

      const requestData = {
        // Used to add add to new wishlist
        user: this.$store.state.session.user.id,
        wishlist: this.wishlist.id,
        deal: this.dealId,
      };

      await this.$store.dispatch('wishlists/saveToList', requestData);

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      await this.$store.dispatch('wishlists/getWishlistsHeaderData', this.wishlist.slug);

      this.$toastr.s(`Saved to <strong>${this.wishlist.name}</strong> wishlist!`);
    },
  },
};
</script>

<style lang="scss">
@import '~/styles/dorado/themes/dorado';

.collection-icon {
  background-color: #fff;
  border-radius: 50%;
  height: 40px;
  padding: 10px;
  margin: auto 0;
  width: 40px;
}

.favorite-count {
  margin-left: 0.5rem;
  font-size: 16px;
}

.icon-heart {
  font-size: 1.3rem;
}

.icon-heart-red path {
  fill: $brick;
}

@media (max-width: 991px) {
  .favorite-count {
    display: block;
    margin: 0;
    text-align: center;
  }

  .wishlists-icon-container {
    background-color: $gray-100;
    border-radius: 50%;
    height: 40px;
    padding: 10px;
    margin: auto 0;
    width: 40px;

    &.collection-icon {
      background-color: #fff;
    }
  }
}
</style>
